import React from "react"
import Toggle from "material-ui/Toggle"
import SelectField from "material-ui/SelectField"
import MenuItem from "material-ui/MenuItem"
import { Card, CardHeader, CardText } from "material-ui/Card"
import strings from "../../localization/strings"
import autobind from "class-autobind"
import * as  accountActions from "../../actions/AccountActions"
import { IHierarchyItemForAccountLevel, IHierarchyItem, IHierarchyItemSettings, IJobDocument } from "../../models"
import * as utils from "../../helpers/utils"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import AppStateStore from "../../stores/AppStateStore"
import AccountStore from "../../stores/AccountStore"
import _ from "lodash"
import HierarchyJobVariations from "./hierarchy_job_variations/HierarchyJobVariations"

interface IProps {
    hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument
    inheritedHierarchyJobItem: IHierarchyItem | IJobDocument
    readonly: boolean
    isJobSettings: boolean
    handleUpdateJob(job: IJobDocument): Promise<void>
}
interface IState {
    expand: boolean
    isRoot: boolean
    canOverride: boolean
    otPolicyId: string
}

export default class HierarchySettingsOTPolicy extends React.Component<IProps, IState>  {
    private notOTPolicyId = "notOTPolicyId"
    constructor(props) {
        super(props)
        this.state = {
            expand: false,
            isRoot: true,
            canOverride: false,
            otPolicyId: this.notOTPolicyId
        }
        autobind(this)
    }

    componentDidMount() {
        this.checkIsRoot(this.props.hierarchyJobItem)
        this.getOTPolicyId(this.props.inheritedHierarchyJobItem)
    }

    componentWillReceiveProps(nextProps: IProps) {
        !_.isEqual(this.props.hierarchyJobItem, nextProps.hierarchyJobItem) &&
            this.checkIsRoot(nextProps.hierarchyJobItem)
        !_.isEqual(this.props.inheritedHierarchyJobItem, nextProps.inheritedHierarchyJobItem) &&
            this.getOTPolicyId(nextProps.inheritedHierarchyJobItem)
    }

    checkIsRoot(hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument) {
        hierarchyJobItem = hierarchyJobItem as IHierarchyItemForAccountLevel
        this.setState({ isRoot: hierarchyJobItem && hierarchyJobItem.level && hierarchyJobItem.level === -1 ? true : false }, () =>
            this.getCanOverride()
        )
    }

    getCanOverride() {
        if (!this.state.isRoot) {
            const hierarchyItem = this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)
            const inheritedHierarchyJobItemId = this.props.inheritedHierarchyJobItem ? this.props.inheritedHierarchyJobItem.Id : null
            this.setState({ canOverride: hierarchyItem.Id === inheritedHierarchyJobItemId })
        }
        else {
            this.setState({ canOverride: false })
        }
    }

    getOTPolicyId(inheritedHierarchyItem: IHierarchyItem | IJobDocument) {
        const hierarchySettings = inheritedHierarchyItem ?
            inheritedHierarchyItem.HierarchyItemSettings :
            AccountStore.getCurrentAccount().HierarchyItemSettings
        this.setState({
            otPolicyId: hierarchySettings.OTPolicySettings && hierarchySettings.OTPolicySettings.OTPolicyId ?
                hierarchySettings.OTPolicySettings.OTPolicyId :
                this.notOTPolicyId
        })
    }

    handleSave(canOverride, otPolicyId) {
        this.setState({ canOverride, otPolicyId: otPolicyId ? otPolicyId : this.notOTPolicyId }, () =>
            this.state.isRoot ?
                this.updateAccountOTPolicySettings() :
                this.updateHierarchyJobItemOTPolicySettings()
        )
    }

    updateAccountOTPolicySettings() {
        let hierarchyItemSettings = AccountStore.getCurrentAccount().HierarchyItemSettings
        if (hierarchyItemSettings) {
            hierarchyItemSettings.OTPolicySettings = { OTPolicyId: this.state.otPolicyId === this.notOTPolicyId ? null : this.state.otPolicyId }
        } else {
            hierarchyItemSettings = { OTPolicySettings: { OTPolicyId: this.state.otPolicyId === this.notOTPolicyId ? null : this.state.otPolicyId } } as IHierarchyItemSettings
        }

        if (!hierarchyItemSettings.OTPolicySettings.OTPolicyId) {
            hierarchyItemSettings.OTPolicySettings = null
        }

        accountActions.patchAccountHierarchySettings(AccountStore.getCurrentAccount().Id, { HierarchyItemSettings: hierarchyItemSettings }, utils.guid())
    }

    updateHierarchyJobItemOTPolicySettings() {
        const hierarchyJobItem = (this.props.hierarchyJobItem) as (IHierarchyItem | IJobDocument)
        if (this.state.canOverride) {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings.OTPolicySettings = { OTPolicyId: this.state.otPolicyId === this.notOTPolicyId ? null : this.state.otPolicyId }
            } else {
                hierarchyJobItem.HierarchyItemSettings = { OTPolicySettings: { OTPolicyId: this.state.otPolicyId === this.notOTPolicyId ? null : this.state.otPolicyId } } as IHierarchyItemSettings
            }
        } else {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings.OTPolicySettings = null
            } else {
                hierarchyJobItem.HierarchyItemSettings = { OTPolicySettings: null } as IHierarchyItemSettings
            }
        }

        if (hierarchyJobItem.HierarchyItemSettings.OTPolicySettings && hierarchyJobItem.HierarchyItemSettings.OTPolicySettings.OTPolicyId == null) {
            hierarchyJobItem.HierarchyItemSettings.OTPolicySettings = null
        }

        hierarchyJobItem.hasOwnProperty("AccountId") ?
            this.props.handleUpdateJob(hierarchyJobItem as IJobDocument).then(() => AppStateStore.setProgressSnackbarContent(null)) :
            accountActions.updateAccountHierarchyItem(AccountStore.getCurrentAccount().Id, hierarchyJobItem, true, utils.guid())
    }

    render() {
        return (
            <Card
                data-intercom-target={"menu-Hierarchy-Settings-Pay"}
                expanded={this.state.expand}
                onExpandChange={expand => this.setState({ expand })}
            >
                <CardHeader
                    title={strings.OT_Policy}
                    subtitle={[
                        <span key="title">
                            {this.state.isRoot ?
                                `(${strings.not_inherited})` :
                                !this.props.inheritedHierarchyJobItem ?
                                    `(${strings.formatString(strings.inherited_from_Account_x, AccountStore.getCurrentAccount().Name)})` :
                                    this.props.inheritedHierarchyJobItem.Id === (this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)).Id ?
                                        `(${strings.not_inherited})` :
                                        `(${strings.formatString(strings.inherited_from_x, `${hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(this.props.inheritedHierarchyJobItem.Id).Label}:  ${(this.props.inheritedHierarchyJobItem as IHierarchyItem).Label}`)})`
                            }
                        </span>,
                        this.state.expand &&
                        <HierarchyJobVariations
                            key="hierarchy job variations"
                            settingPropertyName="OTPolicySettings"
                            hierarchyJobItem={this.props.hierarchyJobItem}
                            inheritedHierarchyJobItem={this.props.inheritedHierarchyJobItem}
                        />
                    ]}
                    actAsExpander
                    showExpandableButton
                    titleStyle={{ fontSize: 20, fontWeight: 500, display: "initial" }}
                    subtitleStyle={{ fontSize: 13, color: "rgb(97, 97, 97)", marginLeft: 10, display: "initial" }}
                />
                <CardText expandable>
                    {!this.state.isRoot &&
                        <Toggle
                            data-intercom-target={"menu-Hierarchy-Settings-Pay-Toggle-Override"}
                            label={strings.Override}
                            style={{ marginBottom: 5, width: 250 }}
                            onToggle={(e, canOverride) =>
                                AppStateStore.setConfirmDialogContent({
                                    content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                    confirm: () => canOverride ?
                                        this.setState({ canOverride }, () => this.updateHierarchyJobItemOTPolicySettings()) :
                                        this.handleSave(canOverride, this.state.otPolicyId)
                                })
                            }
                            toggled={this.state.canOverride}
                            disabled={this.props.readonly}
                        />
                    }
                    <SelectField
                        data-intercom-target={"menu-Hierarchy-Settings-OTPolicy-Select-OTPoilcy"}
                        floatingLabelText={strings.OT_Policy}
                        style={{ width: 250 }}
                        value={this.state.otPolicyId}
                        onChange={(event, key, otPolicyId) =>
                            AppStateStore.setConfirmDialogContent({
                                content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                confirm: () => this.state.otPolicyId !== otPolicyId &&
                                    this.handleSave(this.state.canOverride, otPolicyId)
                            })
                        }
                        disabled={(this.state.isRoot || this.state.canOverride) && !this.props.readonly ? false : true}
                    >
                        <MenuItem
                            value={this.notOTPolicyId}
                            primaryText={strings.None_OT_Policy}
                        />
                        {AccountStore.getCurrentAccount().OTPolicies.map((item, key) =>
                            <MenuItem
                                value={item.Id}
                                primaryText={item.Name}
                                key={key}
                            />
                        )}
                    </SelectField>
                </CardText>
            </Card>
        )
    }
}